import { gsap, ScrollTrigger } from 'gsap/all'
import { breakpoint } from 'helpers'

export const scrollers = {
  el: {
    accordionItems: <NodeListOf<HTMLElement>>(
      document.querySelectorAll('.accordion__item-wrapper')
    ),
    cardsBlocks: <NodeListOf<HTMLElement>>document.querySelectorAll('.cards-block'),
    callToActionBlocks: <NodeListOf<HTMLElement>>(
      document.querySelectorAll('.call-to-action-block')
    ),
    galleryBlocks: <NodeListOf<HTMLElement>>document.querySelectorAll('.gallery-block'),
    graphCards: <NodeListOf<HTMLElement>>document.querySelectorAll('.card--graph'),
    heroes: <NodeListOf<HTMLElement>>document.querySelectorAll('.hero'),
    heroNumbers: <NodeListOf<HTMLElement>>document.querySelectorAll('.hero-numbers'),
    imageBlocks: <NodeListOf<HTMLElement>>document.querySelectorAll('.image-block'),
    imageMainContentBlocks: <NodeListOf<HTMLElement>>document.querySelectorAll('.image-main-content-block'),
    infoBlocks: <NodeListOf<HTMLElement>>document.querySelectorAll('.info-block'),
    logosBlocks: <NodeListOf<HTMLElement>>document.querySelectorAll('.logos-block'),
    numberCards: <NodeListOf<HTMLElement>>document.querySelectorAll('.card--number'),
    subscriptionBlocks: <NodeListOf<HTMLElement>>(
      document.querySelectorAll('.subscription-block')
    ),
    videoBlocks: <NodeListOf<HTMLElement>>document.querySelectorAll('.video-block'),
  },

  init(): void {
    this.el.accordionItems.forEach((wrapper): void => {
      this.animateAccordionItem(wrapper)
    })

    this.el.callToActionBlocks.forEach((wrapper): void => {
      this.animateCallToActionBlock(wrapper)
    })

    this.el.cardsBlocks.forEach((wrapper): void => {
      this.animateCardsBlock(wrapper)
    })

    this.el.galleryBlocks.forEach((wrapper): void => {
      this.animateGalleryBlock(wrapper)
    })

    this.el.graphCards.forEach((card): void => {
      this.animateGraphCard(card)
    })

    this.el.heroes.forEach((wrapper): void => {
      this.animateHero(wrapper)
    })

    this.el.heroNumbers.forEach((wrapper): void => {
      this.animateHeroNumbers(wrapper)
    })

    this.el.imageBlocks.forEach((wrapper): void => {
      this.animateImageBlock(wrapper)
    })

    this.el.imageMainContentBlocks.forEach((wrapper): void => {
      this.animateImageMainContentBlock(wrapper)
    })

    this.el.infoBlocks.forEach((wrapper): void => {
      this.animateInfoBlock(wrapper)
    })

    this.el.logosBlocks.forEach((wrapper): void => {
      this.animateLogosBlock(wrapper)
    })

    this.el.numberCards.forEach((card): void => {
      this.animateNumberCard(card)
    })

    this.el.subscriptionBlocks.forEach((wrapper): void => {
      this.animateSubscriptionBlock(wrapper)
    })

    this.el.videoBlocks.forEach((wrapper): void => {
      this.animateVideoBlock(wrapper)
    })
  },

  animateAccordionItem(wrapper): void {
    gsap.matchMedia().add(
      {
        isDesktop: `(min-width: ${breakpoint.tablet}px)`,
        isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
      },
      (context) => {
        ScrollTrigger.create({
          trigger: wrapper,
          scrub: 1.25,
          start: '0 87%',
          end: context.conditions.isDesktop ? '+=15%' : '+=30%',
          animation: gsap.fromTo(
            wrapper.firstElementChild,
            { opacity: 0, y: 123 },
            {
              ease: 'power2.out',
              opacity: 1,
              y: 0,
            }
          ),
        })
      }
    )
  },

  animateCallToActionBlock(wrapper): void {
    const button = wrapper.querySelector('.call-to-action-block__button')

    if (button) {
      gsap.matchMedia().add(
        {
          isDesktop: `(min-width: ${breakpoint.tablet}px)`,
          isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
        },
        (context) => {
          ScrollTrigger.create({
            trigger: button,
            scrub: 1.25,
            start: context.conditions.isDesktop ? '0 75%' : '0 87%',
            end: context.conditions.isDesktop ? '+=15%' : '+=30%',
            animation: gsap.fromTo(button, { opacity: 0 }, { ease: 'none', opacity: 1 }),
          })
        }
      )
    }
  },

  animateCardsBlock(wrapper): void {
    const cards = wrapper.querySelectorAll('.card')
    const button = wrapper.querySelector('.cards-block__button')

    if (cards.length) {
      gsap.matchMedia().add(
        {
          isLargeDesktop: `(min-width: ${breakpoint.desktop.s}px)`,
          isSmallDesktop: `(min-width: ${breakpoint.tablet}px) and (max-width: ${
            breakpoint.desktop.s - 1
          }px)`,
          isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
        },
        (context) => {
          const { isLargeDesktop, isSmallDesktop, isMobile } = context.conditions
          cards.forEach((card, index): void => {
            ScrollTrigger.create({
              trigger: card,
              scrub: 1.25,
              start: '0 87%',
              end: isLargeDesktop ? '+=15%' : '+=30%',
              animation: gsap.fromTo(
                card.firstElementChild,
                { opacity: 0, y: 123 },
                {
                  ease: 'power2.out',
                  opacity: 1,
                  stagger: () => {
                    const isInfoCard = card.classList.contains('card--info')
                    let stagger

                    switch (true) {
                      case isLargeDesktop && !isInfoCard:
                        stagger = index % 3 === 0 ? 0 : index % 3 === 1 ? 0.1 : 0.2
                        break
                      case (isLargeDesktop && isInfoCard) || isSmallDesktop:
                        stagger = index % 2 === 0 ? 0 : 0.1
                        break
                      case isMobile:
                        stagger = 0
                        break
                      default:
                        stagger = 0
                        break
                    }

                    return stagger
                  },
                  y: 0,
                }
              ),
            })
          })
        }
      )
    }

    if (button) {
      ScrollTrigger.create({
        trigger: button,
        scrub: 1.25,
        start: '0 87%',
        end: '+=15%',
        animation: gsap.fromTo(button, { opacity: 0 }, { ease: 'none', opacity: 1 }),
      })
    }
  },

  animateGraphCard(card): void {
    const graph = <HTMLElement>card.querySelector('.card__graph')
    const graphRing = <HTMLElement>card.querySelector('.card__graph-ring')
    const graphPercentageText = <HTMLElement>(
      card.querySelector('.card__graph-percentage p span')
    )
    const percentage = parseFloat(graph.dataset.percentage)
    const counter = { from: 0, to: percentage }

    if (graphRing) {
      gsap.matchMedia().add(
        {
          isDesktop: `(min-width: ${breakpoint.tablet}px)`,
          isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
        },
        (context) => {
          ScrollTrigger.create({
            trigger: card,
            scrub: 1.25,
            start: '0 87%',
            end: context.conditions.isDesktop ? '+=30%' : '+=50%',
            animation: gsap.fromTo(
              graphRing,
              {
                drawSVG: `0 0`,
                rotation: -90,
                transformOrigin: '50% 50%',
              },
              {
                drawSVG: `0 ${percentage}%`,
              }
            ),
          })
        }
      )
    }

    if (graphPercentageText) {
      ScrollTrigger.create({
        trigger: card,
        scrub: 1.25,
        start: '0 87%',
        end: '+=25%',
        animation: gsap.to(
          counter,
          {
            from: counter.to.toFixed(2),
            onUpdate: () => {
              graphPercentageText.innerText = counter.from.toString()
            },
            roundProps: 'from',
          },
          0
        ),
      })
    }
  },

  animateHero(wrapper): void {
    const images = wrapper.querySelectorAll('.hero__image')
    const text = wrapper.querySelectorAll('.hero-text')
    const textElements = wrapper.querySelectorAll('.hero-text > *')

    if (images.length) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1,
        start: '0% 100%',
        end: '100% 0',
        animation: gsap.fromTo(images, { y: -123 }, { ease: 'none', y: 0 }),
      })
    }

    if (text.length && textElements.length) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1.25,
        start: '0 0',
        end: '100% 0',
        animation: gsap
          .timeline()
          .fromTo(text, { y: 0 }, { ease: 'none', y: -123 }, 0)
          .fromTo(textElements, { y: 0 }, { ease: 'none', y: (index) => 33 * index }, 0),
      })
    }
  },

  animateHeroNumbers(wrapper): void {
    const numbersWrapper = wrapper.querySelector('.hero-numbers__numbers')
    const numbers = wrapper.querySelectorAll('.hero-numbers__number')
    const shapes = wrapper.querySelector('.hero-numbers__shapes')
    const shapesSvg = (nr) => shapes.firstElementChild.children[nr]

    if (numbersWrapper && numbers.length) {
      gsap.matchMedia().add(
        {
          isDesktop: `(min-width: ${breakpoint.tablet}px)`,
          isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
        },
        (context) => {
          const { isDesktop } = context.conditions
          ScrollTrigger.create({
            trigger: numbersWrapper,
            scrub: 1.25,
            start: isDesktop ? '0 87%' : '0 75%',
            end: isDesktop ? '+=15%' : '+=30%',
            animation: gsap.fromTo(
              numbers,
              { opacity: 0, x: isDesktop ? 33 : 50 },
              { ease: 'power2.out', opacity: 1, stagger: 0.1, x: 0 }
            ),
          })
        }
      )
    }

    if (shapes) {
      ScrollTrigger.create({
        trigger: shapes,
        scrub: 1.25,
        start: '0 100%',
        end: '+=120%',
        animation: gsap
          .timeline({
            defaults: { ease: 'none', transformOrigin: '50% 50%' },
          })
          .to(shapesSvg(1), { scale: 0.5 }, 0)
          .to(shapesSvg(2), { rotation: -20, transformOrigin: '0 100%' }, 0)
          .to(shapesSvg(3), { x: -30 }, 0)
          .to(shapesSvg(8), { scale: 1.2 }, 0)
          .to(shapesSvg(9), { x: 20 }, 0)
          .to(shapesSvg(10), { x: -20 }, 0)
          .to(shapesSvg(11), { x: 20, y: -20 }, 0),
      })
    }
  },

  animateImageBlock(wrapper): void {
    const image = wrapper.querySelector('.image-block__image')

    if (image) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1,
        start: '0% 100%',
        end: '100% 0',
        animation: gsap.fromTo(image, { y: -77 }, { ease: 'none', y: 0 }),
      })
    }
  },

  animateImageMainContentBlock(wrapper): void {
    const image = wrapper.querySelector('.image-main-content-block__image')

    if (image) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1,
        start: '0% 100%',
        end: '100% 0',
        animation: gsap.fromTo(image, { y: -77 }, { ease: 'none', y: 0 }),
      })
    }
  },

  animateGalleryBlock(wrapper): void {
    const images = wrapper.querySelectorAll('.gallery-block__picture-container')

    if (images.length) {
      gsap.matchMedia().add(
        {
          isDesktop: `(min-width: ${breakpoint.tablet}px)`,
          isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
        },
        (context) => {
          const { isDesktop } = context.conditions
          ScrollTrigger.create({
            trigger: images,
            scrub: 1.25,
            start: '0 87%',
            end: isDesktop ? '+=15%' : '+=30%',
            animation: gsap.fromTo(
              images,
              { opacity: 0, y: 77 },
              { ease: 'power2.out', opacity: 1, stagger: 0.1, y: 0 }
            ),
          })
        }
      )
    }
  },

  animateInfoBlock(wrapper): void {
    const image = wrapper.querySelector('.info-block__image')
    const textColumn = wrapper.querySelector('.info-block__text-column')
    const textElements = wrapper.querySelectorAll('.info-block__text-column > *')

    if (image) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1,
        start: '0% 100%',
        end: '100% 0',
        animation: gsap.fromTo(image, { y: -77 }, { ease: 'none', y: 0 }),
      })
    }

    if (textElements.length) {
      gsap.matchMedia().add(
        {
          isDesktop: `(min-width: ${breakpoint.tablet}px)`,
          isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
        },
        (context) => {
          const { isDesktop } = context.conditions
          ScrollTrigger.create({
            trigger: isDesktop ? wrapper : textColumn,
            scrub: 1.25,
            start: isDesktop ? '0 70%' : '0 87%',
            end: isDesktop ? '+=50%' : '+=30%',
            animation: gsap.fromTo(
              textElements,
              { opacity: 0, y: 123 },
              { ease: 'power2.out', opacity: 1, stagger: 0.1, y: 0 }
            ),
          })
        }
      )
    }
  },

  animateLogosBlock(wrapper): void {
    const logos = wrapper.querySelectorAll('.logos-block__logo')

    if (logos.length) {
      gsap.matchMedia().add(
        {
          isDesktop: `(min-width: ${breakpoint.tablet}px)`,
          isMobile: `(max-width: ${breakpoint.tablet - 1}px)`,
        },
        (context) => {
          const { isDesktop } = context.conditions
          ScrollTrigger.create({
            trigger: logos,
            scrub: 1.25,
            start: '0 87%',
            end: isDesktop ? '+=15%' : '+=30%',
            animation: gsap.fromTo(
              logos,
              { opacity: 0, x: isDesktop ? 33 : 50 },
              { ease: 'power2.out', opacity: 1, stagger: 0.1, x: 0 }
            ),
          })
        }
      )
    }
  },

  animateNumberCard(card): void {
    const numberText = <HTMLElement>card.querySelector('.card__number')
    const counter = { from: 0, to: parseFloat(numberText.dataset.number) }

    if (numberText) {
      ScrollTrigger.create({
        trigger: card,
        scrub: 1.25,
        start: '0 87%',
        end: '+=25%',
        animation: gsap.to(
          counter,
          {
            from: counter.to.toFixed(2),
            onUpdate: () => {
              numberText.innerText = counter.from.toString()
            },
            roundProps: 'from',
          },
          0
        ),
      })
    }
  },

  animateSubscriptionBlock(wrapper): void {
    const logo = wrapper.querySelector('.subscription-block__logo')

    if (logo) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1.25,
        start: '0 87%',
        end: '100% 0',
        animation: gsap.fromTo(logo, { x: '-5%' }, { ease: 'none', x: '5%' }),
      })
    }
  },

  animateVideoBlock(wrapper): void {
    const button = wrapper.querySelector('.video-block__button')
    const image = wrapper.querySelector('.video-block__image')

    if (button) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1,
        start: '50% 90%',
        end: '+=15%',
        animation: gsap.fromTo(
          button,
          { opacity: 0, scale: 2 },
          { opacity: 1, ease: 'none', scale: 1 }
        ),
      })
    }

    if (image) {
      ScrollTrigger.create({
        trigger: wrapper,
        scrub: 1,
        start: '0% 100%',
        end: '100% 0',
        animation: gsap.fromTo(image, { y: -77 }, { ease: 'none', y: 0 }),
      })
    }
  },
}
